import { ActionIcon, Button, Card, Group, NumberInput, Select, Stack, Text, TextInput } from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconPlus, IconTrash } from "@tabler/icons-react";

import type { Schemas } from "../../api/client";

type Config = Schemas["StateConfig"];
type Rule = Schemas["StateRule"];
type Condition = Schemas["Condition"];

export const STATES = [
  { value: "running", label: "Running" },
  { value: "idle", label: "Idle" },
  { value: "setup", label: "Setup" },
  { value: "stopped", label: "Stopped" },
  { value: "fault", label: "Fault" },
];
const OPS = [
  { value: "eq", label: "=" }, { value: "ne", label: "≠" }, { value: "gt", label: ">" },
  { value: "gte", label: "≥" }, { value: "lt", label: "<" }, { value: "lte", label: "≤" },
];

/** Typed value from the text box: true/false, numbers, otherwise text. */
export function parseValue(text: string): Condition["value"] {
  const t = text.trim();
  if (t === "true" || t === "false") return t === "true";
  if (t !== "" && !Number.isNaN(Number(t))) return Number(t);
  return text;
}

const signalKey = (c: Condition) => (c.role ? `role:${c.role}` : `tag:${c.tag ?? ""}`);

/** Form editor for machine-state rules: ordered rules, all conditions of a rule must hold. */
export function RuleBuilder({ value, onChange, tags, disabled }: {
  value: Config; onChange: (c: Config) => void; tags: Schemas["TagDefinitionIn"][]; disabled?: boolean;
}) {
  const rules = value.rules ?? [];
  const signals = [
    ...tags.filter((t) => t.role).map((t) => ({ value: `role:${t.role}`, label: `${t.label} (role ${t.role})` })),
    ...tags.filter((t) => !t.role).map((t) => ({ value: `tag:${t.key}`, label: `${t.label} (${t.key})` })),
  ];
  const setRules = (next: Rule[]) => onChange({ ...value, rules: next });
  const setRule = (i: number, r: Rule) => setRules(rules.map((x, j) => (j === i ? r : x)));
  const setCond = (i: number, k: number, c: Condition) =>
    setRule(i, { ...rules[i], when: rules[i].when.map((x, j) => (j === k ? c : x)) });
  const move = (i: number, d: -1 | 1) => {
    const next = [...rules];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setRules(next);
  };
  const firstSignal = signals[0]?.value ?? "tag:status";
  const newCondition = (): Condition => {
    const [kind, ref] = firstSignal.split(":");
    return { ...(kind === "role" ? { role: ref as Condition["role"] } : { tag: ref }), op: "eq", value: "" };
  };

  return (
    <Stack gap="xs">
      {rules.length === 0 && (
        <Text size="sm" c="dimmed">No rules: this machine type gets no automatic state (manual recording only).</Text>
      )}
      {rules.map((rule, i) => (
        <Card key={i} withBorder p="xs">
          <Group gap="xs" mb={6} wrap="nowrap">
            <Text size="sm" fw={600} w={60}>Rule {i + 1}</Text>
            <Text size="sm">State is</Text>
            <Select size="xs" w={120} disabled={disabled} data={STATES} value={rule.state}
              onChange={(v) => setRule(i, { ...rule, state: (v ?? "idle") as Rule["state"] })} />
            <Text size="sm">when all of:</Text>
            <Group gap={2} ml="auto" wrap="nowrap">
              <ActionIcon variant="subtle" disabled={disabled || i === 0} aria-label="Move rule up" onClick={() => move(i, -1)}><IconArrowUp size={14} /></ActionIcon>
              <ActionIcon variant="subtle" disabled={disabled || i === rules.length - 1} aria-label="Move rule down" onClick={() => move(i, 1)}><IconArrowDown size={14} /></ActionIcon>
              <ActionIcon variant="subtle" color="red" disabled={disabled} aria-label={`Remove rule ${i + 1}`}
                onClick={() => setRules(rules.filter((_, j) => j !== i))}><IconTrash size={14} /></ActionIcon>
            </Group>
          </Group>
          {rule.when.map((c, k) => (
            <Group key={k} gap="xs" ml={60} mb={4} wrap="nowrap">
              <Select size="xs" w={260} disabled={disabled} data={signals} value={signalKey(c)} searchable
                onChange={(v) => {
                  const [kind, ref] = (v ?? firstSignal).split(":");
                  setCond(i, k, { op: c.op, value: c.value, ...(kind === "role" ? { role: ref as Condition["role"], tag: null } : { tag: ref, role: null }) });
                }} />
              <Select size="xs" w={70} disabled={disabled} data={OPS} value={c.op}
                onChange={(v) => setCond(i, k, { ...c, op: (v ?? "eq") as Condition["op"] })} />
              <TextInput size="xs" w={140} disabled={disabled} placeholder="value, e.g. RUNNING, 8, true"
                value={String(c.value)} onChange={(e) => setCond(i, k, { ...c, value: parseValue(e.currentTarget.value) })} />
              <ActionIcon variant="subtle" color="red" disabled={disabled || rule.when.length === 1} aria-label="Remove condition"
                onClick={() => setRule(i, { ...rule, when: rule.when.filter((_, j) => j !== k) })}><IconTrash size={14} /></ActionIcon>
            </Group>
          ))}
          {!disabled && (
            <Button ml={60} size="compact-xs" variant="subtle" leftSection={<IconPlus size={12} />}
              onClick={() => setRule(i, { ...rule, when: [...rule.when, newCondition()] })}>condition</Button>
          )}
        </Card>
      ))}
      <Group align="end">
        {!disabled && (
          <Button size="xs" variant="default" leftSection={<IconPlus size={14} />}
            onClick={() => setRules([...rules, { state: "running", when: [newCondition()] }])}>Add rule</Button>
        )}
        <Select size="xs" w={190} label="Otherwise the state is" disabled={disabled} data={STATES}
          value={value.default_state ?? "idle"} onChange={(v) => onChange({ ...value, default_state: (v ?? "idle") as Config["default_state"] })} />
        <NumberInput size="xs" w={200} label="Offline after no data for (s)" disabled={disabled} min={0} max={86400}
          value={value.offline_after_s ?? 300} onChange={(v) => onChange({ ...value, offline_after_s: Number(v) || 0 })} />
      </Group>
      <Text size="xs" c="dimmed">
        Rules are checked from the top; the first rule whose conditions all hold decides the state. A signal that has not
        been received never matches. Production is counted separately, by the counting method, never from the state.
      </Text>
    </Stack>
  );
}
